import { useState } from "react";

import type { GlobalTrackedFile, KnowledgeBaseGap } from "../api";
import { useAgentLabel } from "../api/hooks/useStatefulAgents";
import ds from "../components/DriveSyncSettingsCard.module.css";
import { driveOpenUrl } from "../utils/driveOpenUrl";
import { formatDayLong } from "../utils/formatRelative";
import { describeClient, describeLoadFor } from "./globalDataLabels";
import { GlobalDriveFileStatePill } from "./GlobalDriveFileStatePill";
import styles from "./GlobalKnowledgeBases.module.css";

const loadedSize = (file: GlobalTrackedFile): string => {
  const { loaded_chars: chars, loaded_tokens: tokens } = file.load;
  if (chars === null || tokens === null) return "Not synced yet";
  return `~${tokens.toLocaleString()} tokens (${chars.toLocaleString()} characters) in each turn it is loaded into, its Cited sources table left out`;
};

const KnowledgeBaseRow = ({ file }: { file: GlobalTrackedFile }) => {
  const { agentLabel } = useAgentLabel();
  const [showProjects, setShowProjects] = useState(false);
  const kb = file.knowledge_base;
  const projects = file.load.projects;
  if (!kb) return null;
  return (
    <li className={`${ds.sourceRowBlock} ${styles.kbRow}`}>
      <div className={styles.kbHeader}>
        <span className={styles.kbBadge}>Client knowledge base</span>
        <span className={ds.sourceName}>{file.drive_path}</span>
        <span className={styles.kbActions}>
          <GlobalDriveFileStatePill record={file.synced} />
          <a
            className={ds.linkButton}
            href={driveOpenUrl(file.file_id)}
            target="_blank"
            rel="noreferrer"
          >
            Open in Drive
          </a>
        </span>
      </div>
      <div className={styles.kbMeta}>
        <span className={ds.sourceMeta}>
          For {describeClient(file.tenant_scope, file.landlord_scope)} · kept by{" "}
          {agentLabel(kb.kept_by)} · {kb.write_count} revision
          {kb.write_count === 1 ? "" : "s"}, last by{" "}
          {agentLabel(kb.last_written_by ?? kb.kept_by)} on{" "}
          {formatDayLong(kb.last_written_at)}
        </span>
        <span className={ds.sourceMeta}>{describeLoadFor(file.load_for, agentLabel)}</span>
        <span className={ds.sourceMeta}>{loadedSize(file)}</span>
        {file.load.over_limit_for.length > 0 && (
          <p className={ds.error} role="alert">
            Too much is loaded for {file.load.over_limit_for.map(agentLabel).join(", ")}:
            past the per-turn limit some of their files are listed to read instead of
            loaded. Ask the agent to shorten its knowledge bases.
          </p>
        )}
        <span className={ds.sourceMeta}>
          Reaches {projects.length} project{projects.length === 1 ? "" : "s"}{" "}
          {projects.length > 0 && (
            <button
              type="button"
              className={ds.linkButton}
              onClick={() => setShowProjects((v) => !v)}
            >
              {showProjects ? "Hide" : "Show"}
            </button>
          )}
        </span>
        {showProjects && (
          <ul className={styles.projectList}>
            {projects.map((p) => (
              <li key={p.project_id}>{p.name}</li>
            ))}
          </ul>
        )}
      </div>
    </li>
  );
};

/** The client knowledge bases among the connection's tracked files. Agents create them
 *  and set their client and who they are loaded for, so nothing here edits them. */
export const GlobalKnowledgeBasesSection = ({
  files,
  gaps,
}: {
  files: GlobalTrackedFile[];
  gaps: KnowledgeBaseGap[];
}) => {
  const bases = files.filter((f) => f.knowledge_base !== null);
  return (
    <>
      <div className={ds.sectionHeader}>
        <span className={ds.label}>Client knowledge bases ({bases.length})</span>
      </div>
      <p className={ds.helpText}>
        What an agent has learned about one client across its projects, kept as a document
        in Drive. Only agents create them, in a conversation, and the agent sets which
        client each is for and which agents it is loaded for: they cannot be added, edited
        or removed here. On each of the client&apos;s projects a knowledge base is loaded in
        full into every turn of those agents; other agents see it listed.
      </p>
      {bases.length === 0 ? (
        <p className={ds.muted}>No client knowledge bases yet.</p>
      ) : (
        <ul className={ds.sourceList}>
          {bases.map((file) => (
            <KnowledgeBaseRow key={file.file_id} file={file} />
          ))}
        </ul>
      )}
      {gaps.length > 0 && (
        <div className={styles.gaps}>
          <span className={ds.label}>Clients on two or more projects with no knowledge base</span>
          <p className={ds.helpText}>
            Ask an agent in a conversation on one of the client&apos;s projects to write one.
          </p>
          <ul className={styles.gapList}>
            {gaps.map((g) => (
              <li key={`${g.kind}:${g.client}`}>
                {g.kind} {g.client} ({g.project_count} projects)
              </li>
            ))}
          </ul>
        </div>
      )}
    </>
  );
};
